import { PDFDocument, PDFName, PDFArray, PDFDict, PDFRef, PDFStream, pushGraphicsState, popGraphicsState, concatTransformationMatrix, drawObject } from 'pdf-lib';

import { generateMissingAppearances } from './pdfiumEngine';

const numbers = array => array instanceof PDFArray ? array.asArray().map(value => value.asNumber()) : null;

export function appearancePlacementMatrix(bbox, matrix, rect) {
  const m = matrix || [1, 0, 0, 1, 0, 0];
  if (bbox.length !== 4 || m.length !== 6 || rect.length !== 4 || ![...bbox, ...m, ...rect].every(Number.isFinite)) {
    throw new Error('An annotation has invalid appearance coordinates. No changes were saved.');
  }
  const corners = [[bbox[0], bbox[1]], [bbox[2], bbox[1]], [bbox[2], bbox[3]], [bbox[0], bbox[3]]]
    .map(([x, y]) => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]);
  const xs = corners.map(point => point[0]), ys = corners.map(point => point[1]);
  const left = Math.min(...xs), bottom = Math.min(...ys);
  const width = Math.max(...xs) - left, height = Math.max(...ys) - bottom;
  if (width <= 0 || height <= 0) throw new Error('An annotation has an empty appearance. No changes were saved.');
  const sx = Math.abs(rect[2] - rect[0]) / width, sy = Math.abs(rect[3] - rect[1]) / height;
  return [sx, 0, 0, sy, Math.min(rect[0], rect[2]) - sx * left, Math.min(rect[1], rect[3]) - sy * bottom];
}

function normalAppearance(ctx, annotation) {
  const ap = annotation.lookupMaybe(PDFName.of('AP'), PDFDict);
  let ref = ap?.get(PDFName.of('N'));
  let stream = ctx.lookup(ref);
  if (stream instanceof PDFDict) {
    // Checkbox/radio appearance dictionaries contain states, not one Form.
    const state = annotation.get(PDFName.of('AS')) || PDFName.of('Off');
    ref = stream.get(state);
    stream = ctx.lookup(ref);
  }
  return { ref, stream };
}

/** Paint the original appearances, in annotation order, without regenerating
 * fonts or rasterizing pages. Missing visible appearances fail before saving. */
export function flattenAppearances(doc, { forms = true, annotations = true, supersededIds = new Set() } = {}) {
  const ctx = doc.context;
  let count = 0;
  for (const page of doc.getPages()) {
    const annots = page.node.Annots();
    if (!annots) continue;
    const retained = [];
    // Copy inherited/shared resources before installing page-local AP names.
    const resources = page.node.Resources()?.clone(ctx) || ctx.obj({});
    const xObjects = resources.lookupMaybe(PDFName.of('XObject'), PDFDict);
    if (xObjects) resources.set(PDFName.of('XObject'), xObjects.clone(ctx));
    page.node.set(PDFName.of('Resources'), resources);
    for (const ref of annots.asArray()) {
      const annotation = ctx.lookup(ref, PDFDict);
      const subtype = annotation.get(PDFName.of('Subtype'))?.toString();
      const widget = subtype === '/Widget';
      if (subtype === '/Link' || !(widget ? forms : annotations)) { retained.push(ref); continue; }
      const name = annotation.get(PDFName.of('NM'))?.decodeText?.() || '';
      const id = name.startsWith('scrivar:') ? name.slice(8) : name.startsWith('scrivar-ff:') ? name.slice(11) : null;
      const flags = ctx.lookup(annotation.get(PDFName.of('F')))?.asNumber?.() || 0;
      const rect = numbers(annotation.lookupMaybe(PDFName.of('Rect'), PDFArray));
      // Popups have no static page appearance. Hidden annotations stay hidden.
      if (supersededIds.has(id) || subtype === '/Popup' || flags & 35 || rect && (rect[0] === rect[2] || rect[1] === rect[3])) {
        count++; continue;
      }
      const appearance = normalAppearance(ctx, annotation);
      if (!(appearance.stream instanceof PDFStream) || !rect) {
        throw new Error('An annotation or form field has no usable appearance. No content was removed; save a regular copy before flattening this document.');
      }
      const bbox = numbers(appearance.stream.dict.lookupMaybe(PDFName.of('BBox'), PDFArray));
      const matrix = numbers(appearance.stream.dict.lookupMaybe(PDFName.of('Matrix'), PDFArray));
      if (!bbox) throw new Error('An annotation is missing its appearance bounds. No changes were saved.');
      const appearanceRef = appearance.ref instanceof PDFRef ? appearance.ref : ctx.register(appearance.stream);
      const resourceName = page.node.newXObject('FlatAP', appearanceRef);
      page.pushOperators(pushGraphicsState(), concatTransformationMatrix(...appearancePlacementMatrix(bbox, matrix, rect)), drawObject(resourceName), popGraphicsState());
      count++;
    }
    if (retained.length) page.node.set(PDFName.of('Annots'), ctx.obj(retained));
    else page.node.delete(PDFName.of('Annots'));
  }
  if (forms && count) doc.catalog.delete(PDFName.of('AcroForm'));
  return count;
}

/** Delete unreachable objects only; a shared appearance may still be used by
 * another page, so deleting its ref while removing an annotation is unsafe. */
export function removeUnreachableObjects(doc) {
  const ctx = doc.context, refs = new Set(), objects = new Set();
  const visit = object => {
    if (object instanceof PDFRef) {
      if (refs.has(object.toString())) return;
      refs.add(object.toString()); visit(ctx.lookup(object)); return;
    }
    if (!object || objects.has(object)) return;
    objects.add(object);
    if (object instanceof PDFArray) object.asArray().forEach(visit);
    else if (object instanceof PDFDict) object.entries().forEach(([, value]) => visit(value));
    else if (object instanceof PDFStream) visit(object.dict);
  };
  Object.values(ctx.trailerInfo).forEach(visit);
  for (const [ref] of ctx.enumerateIndirectObjects()) if (!refs.has(ref.toString())) ctx.delete(ref);
}

/** Most PDFs already carry their exact appearance. Generate only missing
 * appearances, so ordinary flattening never substitutes existing fonts. */
export async function ensureFlattenAppearances(doc, { forms = true, annotations = true, supersededIds = new Set() } = {}) {
  const targets = [];
  for (const [pageIndex, page] of doc.getPages().entries()) {
    for (const [annotationIndex, ref] of (page.node.Annots()?.asArray() || []).entries()) {
      const annotation = doc.context.lookup(ref, PDFDict);
      const subtype = annotation.get(PDFName.of('Subtype'))?.toString();
      const widget = subtype === '/Widget';
      const flags = doc.context.lookup(annotation.get(PDFName.of('F')))?.asNumber?.() || 0;
      const name = annotation.get(PDFName.of('NM'))?.decodeText?.() || '';
      const id = name.startsWith('scrivar:') ? name.slice(8) : name.startsWith('scrivar-ff:') ? name.slice(11) : null;
      const rect = numbers(annotation.lookupMaybe(PDFName.of('Rect'), PDFArray));
      if (['/Link', '/Popup'].includes(subtype) || !(widget ? forms : annotations) || flags & 35 || supersededIds.has(id) ||
          rect && (rect[0] === rect[2] || rect[1] === rect[3])) continue;
      if (!(normalAppearance(doc.context, annotation).stream instanceof PDFStream)) targets.push({ pageIndex, annotationIndex, widget });
    }
  }
  if (!targets.length) return doc;
  const bytes = await doc.save({ updateFieldAppearances: false });
  return PDFDocument.load(await generateMissingAppearances(bytes, targets));
}
